import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { APP_ROUTES } from './app.config.vars/routes';
import Rallies from './pages/GameMode/Rallies';
import RallyPreview from './pages/GameMode/RallyPreview';
import RallyRegistration from './pages/GameMode/RallyRegistration';
import RallyGamePlay from './pages/GameMode/Gameplay';
import Redirect from './pages/GameMode/Redirect';
import { getRalliesTargetGroups, getRalliesThemes } from './api/game-mode/rallies-filter-options';
import { useDispatch, useSelector } from 'react-redux';
import { getLocationFilter, getIsTeamRally, getSelectTargetGroupId, getSelectThemeId, setRallies, setRalliesTargetGroups, setRalliesThemes, } from './store/slices/ralliesPreview';
import { useTranslation } from 'react-i18next';
import { getRallies } from './api/game-mode/rallies';
import RallyGameResultPage from './pages/GameMode/GameResult';
import { useDataSync } from './hooks/syncData';
import Modal from './ui/Modal';
import { GAME_DB } from './db/game';
import { setGameStatus } from './store/slices/rallyGame';
import { useOfflineModeDetector } from './hooks/offlineModeDetector';
import { STATIC_IMAGES } from './app.config.vars/staticImages';
import Login from './pages/Admin/Login';
import ForgotPassword from './pages/Admin/ForgotPassword';
import ResetPassword from './pages/Admin/ResetPassword';
import ManageRallies from './pages/Admin/ManageRallies';
import PrivateRoute from './components/PrivateRoute';
import CreateRally from './pages/Admin/CreateRally';
import { formatLangCode, setViewportHeight } from './helpers';
import EditRally from './pages/Admin/EditRally';
import ManageTasks from './pages/Admin/ManageTasks';
import CreateTask from './pages/Admin/CreateTask';
import EditTask from './pages/Admin/EditTask';
import RallySessions from './pages/Admin/RallySessions';
import RallySessionResult from './pages/Admin/RallySessionResult';
import { APP_LANGUAGES } from './app.config.vars/languages';
import AdminRallyPreviewPage from './pages/Admin/RallyPreview';
import AdminTaskPreviewPage from './pages/Admin/TaskPreview';
import AdminRallyPublishedPage from './pages/Admin/RallyPublished';
const App = () => {
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const setLanguage = async (lang) => {
        await i18n.changeLanguage(lang);
    };
    useEffect(() => {
        const currentLang = APP_LANGUAGES.find((lang) => lang.code === formatLangCode(i18n.language));
        setLanguage(currentLang.code);
    }, []);
    const selectedThemeId = useSelector(getSelectThemeId);
    const selectedTargetGroupId = useSelector(getSelectTargetGroupId);
    const locationFilter = useSelector(getLocationFilter);
    const isTeamRally = useSelector(getIsTeamRally);
    useOfflineModeDetector();
    useDataSync();
    useEffect(() => {
        setViewportHeight();
        const handleResize = () => setViewportHeight();
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);
    useEffect(() => {
        const fetchThemes = async () => {
            try {
                const themes = await getRalliesThemes();
                if (Array.isArray(themes)) {
                    dispatch(setRalliesThemes(themes.map((theme) => ({ label: theme.title, value: String(theme.uid) }))));
                }
            }
            catch (error) {
                console.error('Failed to fetch themes:', error);
            }
        };
        const fetchTargetGroups = async () => {
            try {
                const targetGroups = await getRalliesTargetGroups();
                if (Array.isArray(targetGroups)) {
                    dispatch(setRalliesTargetGroups(targetGroups.map((targetGroup) => ({
                        label: targetGroup.title,
                        value: String(targetGroup.uid),
                    }))));
                }
            }
            catch (error) {
                console.error('Failed to fetch target groups:', error);
            }
        };
        fetchThemes();
        fetchTargetGroups();
    }, [i18n.language]);
    useEffect(() => {
        const fetchRallies = async () => {
            const rallies = await getRallies({
                'themes[]': selectedThemeId,
                locationFilter,
                'targetGroups[]': selectedTargetGroupId,
                isTeamRally,
            });
            dispatch(setRallies(rallies));
        };
        fetchRallies();
    }, [selectedThemeId, selectedTargetGroupId, locationFilter, isTeamRally, i18n.language]);
    useEffect(() => {
        const setActualGameStatus = async () => {
            const actualGameStatus = await GAME_DB.GET.gameStatus();
            dispatch(setGameStatus(actualGameStatus));
        };
        setActualGameStatus();
    }, []);
    useEffect(() => {
        Object.entries(STATIC_IMAGES).forEach(([_, record]) => {
            localStorage.setItem(record.key, record.value);
        });
    }, []);
    return (_jsxs(_Fragment, { children: [_jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(Navigate, { to: "/rallies", replace: true }) }), _jsx(Route, { path: APP_ROUTES.rallies, element: _jsx(Rallies, {}) }), _jsx(Route, { path: APP_ROUTES.rallyPreview, element: _jsx(RallyPreview, {}) }), _jsx(Route, { path: APP_ROUTES.rallyRegistration, element: _jsx(RallyRegistration, {}) }), _jsx(Route, { path: APP_ROUTES.rallyGamePlay, element: _jsx(RallyGamePlay, {}) }), _jsx(Route, { path: APP_ROUTES.rallyGameResult, element: _jsx(RallyGameResultPage, {}) }), _jsx(Route, { path: "*", element: _jsx(Redirect, {}) }), _jsx(Route, { path: APP_ROUTES.adminLogin, element: _jsx(Login, {}) }), _jsx(Route, { path: APP_ROUTES.adminForgotPassword, element: _jsx(ForgotPassword, {}) }), _jsx(Route, { path: APP_ROUTES.adminResetPassword, element: _jsx(ResetPassword, {}) }), _jsx(Route, { path: APP_ROUTES.adminManageRallies, element: _jsx(PrivateRoute, { children: _jsx(ManageRallies, {}) }) }), _jsx(Route, { path: APP_ROUTES.adminCreateRally, element: _jsx(PrivateRoute, { children: _jsx(CreateRally, {}) }) }), _jsx(Route, { path: APP_ROUTES.adminEditRally, element: _jsx(PrivateRoute, { children: _jsx(EditRally, {}) }) }), _jsx(Route, { path: APP_ROUTES.adminManageTasks, element: _jsx(PrivateRoute, { children: _jsx(ManageTasks, {}) }) }), _jsx(Route, { path: APP_ROUTES.adminCreateTask, element: _jsx(PrivateRoute, { children: _jsx(CreateTask, {}) }) }), _jsx(Route, { path: APP_ROUTES.adminEditTask, element: _jsx(PrivateRoute, { children: _jsx(EditTask, {}) }) }), _jsx(Route, { path: APP_ROUTES.adminRallySessions, element: _jsx(PrivateRoute, { children: _jsx(RallySessions, {}) }) }), _jsx(Route, { path: APP_ROUTES.adminRallySessionResults, element: _jsx(PrivateRoute, { children: _jsx(RallySessionResult, {}) }) }), _jsx(Route, { path: APP_ROUTES.adminRallyPreview, element: _jsx(PrivateRoute, { children: _jsx(AdminRallyPreviewPage, {}) }) }), _jsx(Route, { path: APP_ROUTES.adminTaskPreview, element: _jsx(PrivateRoute, { children: _jsx(AdminTaskPreviewPage, {}) }) }), _jsx(Route, { path: APP_ROUTES.adminPublishedRally, element: _jsx(PrivateRoute, { children: _jsx(AdminRallyPublishedPage, {}) }) })] }), _jsx(Modal, {})] }));
};
export default App;
